import { useCallback, useMemo } from 'react';
import {
  trackEvent,
  trackError,
  type AnalyticsEventName,
  type AnalyticsEventParams,
} from '@/lib/analytics';

/**
 * React hook for analytics tracking
 * Provides memoized tracking functions for use in components
 * 
 * @example
 * const { track, trackIdentify, trackVideo, trackShare } = useAnalytics();
 * 
 * // Generic event
 * track('memory_view', { memory_slug: 'abc123', status: 'done' });
 * 
 * // Semantic helpers
 * trackIdentify.start('order-123', 'Restaurant Name');
 * trackVideo.play('memory-slug');
 * trackShare.click('memory-slug');
 */
export function useAnalytics() {
  /**
   * Generic event tracker with type safety
   */
  const track = useCallback(
    <T extends AnalyticsEventName>(
      eventName: T,
      params: T extends keyof AnalyticsEventParams ? AnalyticsEventParams[T] : Record<string, unknown>
    ) => {
      trackEvent(eventName, params);
    },
    []
  );

  /**
   * Track errors with context
   */
  const error = useCallback(
    (context: string, err: Error | string, additionalParams?: Record<string, unknown>) => {
      trackError(context, err, additionalParams);
    },
    []
  );

  // ============================================
  // Semantic helpers for the KOMBU funnel
  // All objects are memoized to prevent infinite re-render loops
  // when used as useEffect dependencies
  // ============================================

  /**
   * Identify flow events
   */
  const trackIdentify = useMemo(() => ({
    start: (orderId: string, restaurant?: string) => {
      trackEvent('identify_start', { order_id: orderId, restaurant });
    },
    submit: (orderId: string, restaurant?: string) => {
      trackEvent('identify_submit', { order_id: orderId, restaurant });
    },
    error: (orderId: string, errorMessage: string) => {
      trackEvent('identify_error', { order_id: orderId, error_message: errorMessage });
    },
  }), []);

  /**
   * Memory flow events
   */
  const trackMemory = useMemo(() => ({
    view: (slug: string, status: string, restaurant?: string) => {
      trackEvent('memory_view', { memory_slug: slug, status, restaurant });
    },
    error: (slug: string, errorMessage: string) => {
      trackEvent('memory_error', { memory_slug: slug, error_message: errorMessage });
    },
  }), []);

  /**
   * Video player events
   */
  const trackVideo = useMemo(() => ({
    ready: (slug: string, restaurant?: string) => {
      trackEvent('video_ready', { memory_slug: slug, restaurant });
    },
    play: (slug: string, restaurant?: string) => {
      trackEvent('video_play', { memory_slug: slug, restaurant });
    },
    pause: (slug: string, currentTime?: number) => {
      trackEvent('video_pause', { memory_slug: slug, current_time: currentTime });
    },
    progress: (slug: string, percent: number) => {
      trackEvent('video_progress', { memory_slug: slug, percent });
    },
    complete: (slug: string, restaurant?: string) => {
      trackEvent('video_complete', { memory_slug: slug, restaurant });
    },
    error: (slug: string, errorMessage: string) => {
      trackEvent('video_error', { memory_slug: slug, error_message: errorMessage });
    },
  }), []);

  /**
   * Share/Download events
   */
  const trackShare = useMemo(() => ({
    click: (slug: string, method?: 'native' | 'fallback') => {
      trackEvent('share_click', { memory_slug: slug, method });
    },
    success: (slug: string, method?: 'file' | 'link') => {
      trackEvent('share_success', { memory_slug: slug, method });
    },
    error: (slug: string, errorMessage: string) => {
      trackEvent('share_error', { memory_slug: slug, error_message: errorMessage });
    },
  }), []);

  const trackDownload = useMemo(() => ({
    click: (slug: string) => {
      trackEvent('download_click', { memory_slug: slug });
    },
    success: (slug: string) => {
      trackEvent('download_success', { memory_slug: slug });
    },
    error: (slug: string, errorMessage: string) => {
      trackEvent('download_error', { memory_slug: slug, error_message: errorMessage });
    },
  }), []);

  /**
   * QR scan event
   */
  const trackQR = useMemo(() => ({
    scan: (orderId: string, restaurant?: string) => {
      trackEvent('qr_scan', { order_id: orderId, restaurant });
    },
  }), []);

  /**
   * Photo upload events
   */
  const trackPhoto = useMemo(() => ({
    start: (slug: string) => {
      trackEvent('photo_upload_start', { memory_slug: slug });
    },
    success: (slug: string) => {
      trackEvent('photo_upload_success', { memory_slug: slug });
    },
    error: (slug: string, errorMessage: string) => {
      trackEvent('photo_upload_error', { memory_slug: slug, error_message: errorMessage });
    },
  }), []);

  /**
   * Rating/Review events
   */
  const trackRating = useMemo(() => ({
    submit: (slug: string, itemId: string, rating: number) => {
      trackEvent('rating_submit', { memory_slug: slug, item_id: itemId, rating });
    },
  }), []);

  const trackReview = useMemo(() => ({
    submit: (slug: string) => {
      trackEvent('review_submit', { memory_slug: slug });
    },
  }), []);

  /**
   * Admin auth events
   */
  const trackAdminAuth = useMemo(() => ({
    loginSuccess: (restaurantId?: string) => {
      trackEvent('admin_login_success', { restaurant_id: restaurantId });
    },
    loginFailure: (errorMessage: string) => {
      trackEvent('admin_login_failure', { error_message: errorMessage });
    },
    logout: (restaurantId?: string) => {
      trackEvent('admin_logout', { restaurant_id: restaurantId });
    },
  }), []);

  /**
   * Admin order events
   */
  const trackAdminOrder = useMemo(() => ({
    view: (orderId: string, restaurantId?: string) => {
      trackEvent('admin_order_view', { order_id: orderId, restaurant_id: restaurantId });
    },
    printThermal: (orderId: string, restaurantId?: string) => {
      trackEvent('admin_order_print_thermal', { order_id: orderId, restaurant_id: restaurantId });
    },
    printBrowser: (orderId: string, restaurantId?: string) => {
      trackEvent('admin_order_print_browser', { order_id: orderId, restaurant_id: restaurantId });
    },
  }), []);

  /**
   * Admin item events
   */
  const trackAdminItem = useMemo(() => ({
    create: (itemId: string, restaurantId?: string) => {
      trackEvent('admin_item_create', { item_id: itemId, restaurant_id: restaurantId });
    },
    edit: (itemId: string, restaurantId?: string) => {
      trackEvent('admin_item_edit', { item_id: itemId, restaurant_id: restaurantId });
    },
    delete: (itemId: string, restaurantId?: string) => {
      trackEvent('admin_item_delete', { item_id: itemId, restaurant_id: restaurantId });
    },
  }), []);

  /**
   * Admin multimedia events
   */
  const trackAdminMultimedia = useMemo(() => ({
    chefVideoUpload: (restaurantId?: string) => {
      trackEvent('admin_chef_video_upload', { restaurant_id: restaurantId });
    },
    chefVideoDelete: (restaurantId?: string) => {
      trackEvent('admin_chef_video_delete', { restaurant_id: restaurantId });
    },
    ambientPhotoUpload: (restaurantId?: string) => {
      trackEvent('admin_ambient_photo_upload', { restaurant_id: restaurantId });
    },
    ambientPhotoDelete: (photoId: string, restaurantId?: string) => {
      trackEvent('admin_ambient_photo_delete', { photo_id: photoId, restaurant_id: restaurantId });
    },
  }), []);

  /**
   * Admin settings events
   */
  const trackAdminSettings = useMemo(() => ({
    save: (restaurantId?: string) => {
      trackEvent('admin_settings_save', { restaurant_id: restaurantId });
    },
  }), []);

  return {
    track,
    error,
    trackIdentify,
    trackMemory,
    trackVideo,
    trackShare,
    trackDownload,
    trackQR,
    trackPhoto,
    trackRating,
    trackReview,
    trackAdminAuth,
    trackAdminOrder,
    trackAdminItem,
    trackAdminMultimedia,
    trackAdminSettings,
  };
}
